// Documents sidebar — extends shared-gov's AppSidebar.
// Top: AppNavbar (cross-app switcher)
// Middle: section nav (Documents, Notes, Knowledge Base) + contextual sub-nav
import { DOC_FOLDERS, KB_CATEGORIES } from '../../state.js'

const _navigate = (el, path) => {
  let rootEl = el
  while (rootEl && !rootEl.routes) rootEl = rootEl.parent
  const router = el.context?.utils?.router
  if (router && rootEl)
    router(path, rootEl, {}, { pushState: true, scrollToTop: true })
}

export const DocsSidebar = {
  extends: 'AppSidebar',

  Top: null,

  Middle: {
    gap: 'X2',
    flow: 'y',
    padding: '0',

    SectionNav: { extends: 'DocsSectionNav', padding: 'X2' },

    Line: {
      height: '1px',
      background: 'line',
      margin: '0'
    },

    DocsSubNav: { padding: 'X2' }
  },

  // Scrollable people rail at the bottom of the sidebar — same groups
  // calendar uses so collaborators are one click away while browsing docs.
  // Key-based auto-extend resolves `PeopleSection` against the registered
  // component, so no explicit `extends:` is needed.
  PeopleSection: {
    TeamGroup: { extends: 'PeopleGroup', label: 'TEAM', groupName: 'team' },
    AdvisorsGroup: {
      extends: 'PeopleGroup',
      label: 'ADVISORS',
      groupName: 'advisor',
      display: (el) => (el.call('currentUserIsSuperadmin') ? 'flex' : 'none')
    },
    InvestorsGroup: {
      extends: 'PeopleGroup',
      label: 'INVESTORS',
      groupName: 'investor',
      display: (el) => (el.call('currentUserIsSuperadmin') ? 'flex' : 'none')
    }
  }
}

// ── Section switcher: Documents / Notes / Knowledge Base ──

const _sections = [
  { key: 'documents', label: 'Documents', icon: 'document' },
  { key: 'notes', label: 'Notes', icon: 'edit' },
  { key: 'knowledge-base', label: 'Knowledge Base', icon: 'document' }
]

export const DocsSectionNav = {
  flow: 'y',
  gap: 'V',

  childExtends: 'DocsSectionRow',
  childrenAs: 'state',
  children: () => _sections
}

export const DocsSectionRow = {
  extends: 'SidebarRow',
  fontWeight: '600',
  background: (el, s) =>
    s.root.activeSection === s.key ? 'overlay' : 'transparent',
  color: (el, s) => (s.root.activeSection === s.key ? 'title' : 'caption'),
  onClick: (e, el, s) => {
    s.root.update({
      activeSection: s.key,
      selectedDocument: null,
      editorDoc: null,
      editorPreview: false
    })
    _navigate(el, '/documents' + (s.key === 'documents' ? '' : '/' + s.key))
  },

  Icon: { icon: (el, s) => s.icon || 'document' },
  Label: { text: (el, s) => s.label || s.key },

  // Section nav doesn't need three-dot menu
  MoreBtn: null
}

// ── Contextual sub-navigation (folders for documents, filters for notes, categories for KB) ──

export const DocsSubNav = {
  flow: 'y',
  gap: 'V',

  FolderList: {
    extends: 'DocsFolderList',
    if: (el, s) => s.root.activeSection === 'documents'
  },

  NotesFilterList: {
    extends: 'DocsNotesFilterList',
    if: (el, s) => s.root.activeSection === 'notes'
  },

  KbCategoryList: {
    extends: 'DocsKbCategoryList',
    if: (el, s) => s.root.activeSection === 'knowledge-base'
  }
}

// ── Document folders ──

export const DocsFolderList = {
  flow: 'y',
  gap: 'V',
  childExtends: 'DocsFolderRow',
  childrenAs: 'state',
  children: (el, s) => DOC_FOLDERS
}

export const DocsFolderRow = {
  extends: 'SidebarRow',
  background: (el, s) =>
    s.root.selectedFolder === s.key ? 'overlay' : 'transparent',
  color: (el, s) => (s.root.selectedFolder === s.key ? 'title' : 'caption'),
  onClick: (e, el, s) => {
    s.root.update({ selectedFolder: s.key, selectedDocument: null })
  },

  Icon: { icon: (el, s) => s.icon || 'document' },
  Label: { text: (el, s) => s.label || s.key },

  Count: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      const all = s.root.documents || []
      const n =
        s.key === 'all'
          ? all.length
          : all.filter((d) => d.folder === s.key).length
      return String(n)
    }
  },

  MoreBtn: {
    items: [
      { label: 'Rename folder', action: 'rename-folder' },
      { label: 'Delete folder', action: 'delete-folder', color: 'red' }
    ]
  }
}

// ── Notes filters ──

const _notesFilters = [
  { key: 'all', label: 'All notes', icon: 'edit' },
  { key: 'member', label: 'Member notes', icon: 'user' },
  { key: 'ai', label: 'AI notes', icon: 'star' }
]

export const DocsNotesFilterList = {
  flow: 'y',
  gap: 'V',
  childExtends: 'DocsNotesFilterRow',
  childrenAs: 'state',
  children: () => _notesFilters
}

export const DocsNotesFilterRow = {
  extends: 'SidebarRow',
  background: (el, s) =>
    s.root.notesFilter === s.key ? 'overlay' : 'transparent',
  color: (el, s) => (s.root.notesFilter === s.key ? 'title' : 'caption'),
  onClick: (e, el, s) => {
    s.root.update({ notesFilter: s.key, selectedNoteId: null })
  },

  Icon: { icon: (el, s) => s.icon || 'edit' },
  Label: { text: (el, s) => s.label || s.key },

  Count: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      const all = s.root.notes || []
      if (s.key === 'all') return String(all.length)
      return String(all.filter((n) => n.source === s.key).length)
    }
  },

  MoreBtn: {
    items: [{ label: 'Customize filter', action: 'customize-filter' }]
  }
}

// ── Knowledge Base categories ──

export const DocsKbCategoryList = {
  flow: 'y',
  gap: 'V',
  childExtends: 'DocsKbCategoryRow',
  childrenAs: 'state',
  children: (el, s) => KB_CATEGORIES
}

export const DocsKbCategoryRow = {
  extends: 'SidebarRow',
  background: (el, s) =>
    s.root.selectedCategory === s.key ? 'overlay' : 'transparent',
  color: (el, s) => (s.root.selectedCategory === s.key ? 'title' : 'caption'),
  onClick: (e, el, s) => {
    s.root.update({ selectedCategory: s.key })
  },

  Icon: { icon: (el, s) => s.icon || 'book' },
  Label: { text: (el, s) => s.label || s.key },

  Count: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      const all = s.root.kbArticles || []
      if (s.key === 'all') return String(all.length)
      return String(all.filter((a) => a.category === s.key).length)
    }
  },

  MoreBtn: {
    items: [
      { label: 'Rename category', action: 'rename-category' },
      { label: 'Delete category', action: 'delete-category', color: 'red' }
    ]
  }
}
